import React, { useState } from 'react';
import { CheckCircle2, ArrowUpRight } from 'lucide-react';
import { RevealCard } from '../Animations';

const LeadMagnet = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const formData = new FormData(e.target as HTMLFormElement);

    try {
      const response = await fetch('https://formspree.io/emergeautomations@gmail.com', {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        const errorData = await response.json();
        if (errorData.code === 'FORM_NOT_FOUND' || response.status === 404) {
          setError(
            'Formspree setup required: Please check your email (emergeautomations@gmail.com) for a confirmation link to activate this form.'
          );
        } else {
          setError(errorData.error || 'Submission failed. Please try again.');
        }
      }
    } catch {
      setError('An error occurred. Please check your connection or try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="py-16 md:py-24 relative">
      <div className="container-custom">
        <RevealCard className="relative rounded-[var(--radius-xl)] border border-white/10 bg-[color:var(--color-surface-1)] overflow-hidden">
          <div
            className="pointer-events-none absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full opacity-60"
            style={{
              background:
                'radial-gradient(closest-side, rgba(198,242,78,0.10), transparent 70%)',
              filter: 'blur(10px)',
            }}
          />
          <div className="relative grid lg:grid-cols-12 gap-10 p-8 md:p-14 items-center">
            <div className="lg:col-span-7">
              <span className="eyebrow eyebrow--accent mb-5 inline-block">
                Free resource
              </span>
              <h2 className="text-[clamp(1.75rem,3.5vw,2.75rem)] font-display font-semibold text-white leading-[1.1] tracking-[-0.025em] mb-5">
                Free AI checklist for 2026
              </h2>
              <p className="text-[16px] text-zinc-400 leading-relaxed max-w-lg">
                Find out exactly which parts of your business are ready for AI automation.
                Download our simple guide for business owners.
              </p>
            </div>
            <div className="lg:col-span-5 w-full">
              {error && (
                <div className="mb-4 p-3 rounded-[var(--radius-sm)] border border-red-500/30 bg-red-500/[0.08] text-red-300 text-xs">
                  {error}
                </div>
              )}
              {!isSubmitted ? (
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    required
                    placeholder="Enter your work email"
                    aria-label="Work email for AI checklist"
                    className="flex-grow min-w-0 px-4 py-3.5 rounded-[var(--radius-md)] bg-[color:var(--color-bg-base)] border border-white/10 text-white placeholder:text-zinc-600 text-[15px] focus:outline-none focus:border-[color:var(--color-accent)] transition-colors"
                  />
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary min-w-[140px] justify-center"
                  >
                    {isLoading ? (
                      <span className="w-4 h-4 border-2 border-[color:var(--color-accent-on)] border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        Get the guide
                        <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
                      </>
                    )}
                  </button>
                </form>
              ) : (
                <div className="flex items-start gap-3 p-5 rounded-[var(--radius-md)] border border-[color:var(--color-accent)]/30 bg-[color:var(--color-accent)]/[0.06]">
                  <CheckCircle2 className="w-5 h-5 text-[color:var(--color-accent)] mt-0.5 shrink-0" strokeWidth={1.75} />
                  <div>
                    <p className="font-medium text-white">Check your inbox.</p>
                    <p className="text-sm text-zinc-400 mt-0.5">
                      We've sent the guide to your email.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </RevealCard>
      </div>
    </section>
  );
};

export default LeadMagnet;
